import { Box } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useElementSize } from '~/hooks/useElementSize';
import type { GridWidget } from '../grid/GridWidget';
import FloatingWidget from './FloatingWidget';
import { openingWidgetFrame, WidgetFrame } from './widgetFrame';

/** Above every overlay a tile draws, so no marker covers a widget. */
const WIDGET_LAYER_Z_INDEX = 10;

interface RowWidgetLayerProps {
    /** In the order they open in. */
    readonly widgets: ReadonlyArray<GridWidget>;
    readonly frames: ReadonlyMap<string, WidgetFrame>;
    readonly onFrameChange: (id: string, frame: WidgetFrame) => void;
}

/**
 * The layer a row's widgets float in. A widget with no frame opens left of
 * those before it and keeps its frame from then on; each is drawn fitted to
 * the row. Between widgets the pointer reaches the panes beneath.
 */
export default function RowWidgetLayer({
    widgets,
    frames,
    onFrameChange,
}: RowWidgetLayerProps) {
    const layerRef = useRef<HTMLDivElement | null>(null);
    const box = useElementSize(layerRef);

    const shownFrames = useMemo(() => {
        const shown = new Map<string, WidgetFrame>();
        if (!box) {
            return shown;
        }
        for (const widget of widgets) {
            const kept = frames.get(widget.id);
            if (kept) {
                shown.set(widget.id, kept);
            }
        }
        for (const widget of widgets) {
            if (!shown.has(widget.id)) {
                shown.set(
                    widget.id,
                    openingWidgetFrame(widget.openingSize, box, [
                        ...shown.values(),
                    ])
                );
            }
        }
        return shown;
    }, [widgets, frames, box]);

    useEffect(() => {
        for (const [id, frame] of shownFrames) {
            if (!frames.has(id)) {
                onFrameChange(id, frame);
            }
        }
    }, [shownFrames, frames, onFrameChange]);

    return (
        <Box
            ref={layerRef}
            data-testid="row-widget-layer"
            sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                zIndex: WIDGET_LAYER_Z_INDEX,
            }}
        >
            {box
                ? widgets.map((widget) => {
                      const frame = shownFrames.get(widget.id);
                      if (!frame) {
                          return null;
                      }
                      return (
                          <FloatingWidget
                              key={widget.id}
                              title={widget.title}
                              subject={widget.subject}
                              actions={widget.actions}
                              frame={frame}
                              box={box}
                              onFrameChange={(next) =>
                                  onFrameChange(widget.id, next)
                              }
                              // A widget opened again opens expanded.
                              onClose={() => {
                                  onFrameChange(widget.id, {
                                      ...frame,
                                      collapsed: false,
                                  });
                                  widget.onClose();
                              }}
                          >
                              {widget.body()}
                          </FloatingWidget>
                      );
                  })
                : null}
        </Box>
    );
}
